import { USER_TYPES } from '@/constants';
import { ICON_THEMES, KEYS_MAP } from '@/constants/calendar';
import { leadingZero } from '@/utils';

/**
 * @param {Object} student
 * @returns {Object}
 */
export const studentMapper = ({ id, attributes }) => {
  // const DESC_LESSONS = {
  //   costOfEducation: 'стоимость обучения',
  //   leftToPay: 'осталось оплатить',
  //   inPackage: 'занятий в пакете обучения',
  //   left: 'доступно вождений',
  //   additional: 'дополнительные занятия',
  //   // При необходимости переименовать переменную timeBegin
  //   // нужно это также сделать в PageStudentAccount\getStudentRegistrationTime
  //   timeBegin: 'запись доступна с',
  // };
  let lessons = {};
  if (attributes.lessons) {
    lessons = attributes.lessons;
  }
  return {
    id,
    ...attributes,
    lessons,
  };
};

/**
 * @param {Object} instructor
 * @returns {Object}
 */
export const instructorMapper = ({ id, attributes }) => ({
  id,
  ...attributes,
});

/**
 * @param {Array} legend
 * @returns {Array}
 */
export const legendMapper = (legend) => legend.map(({ id, attributes }) => ({
  id,
  ...attributes,
}));

/**
 * Маппер календаря базовый
 * Форматирование данных, настройка базовых флагов интерфейса и стилей
 * @param {Array} calendar
 * @returns {Array}
 */
export const calendarDefaultMapper = (calendar) => calendar.map(({ id, attributes }) => {
  const date = leadingZero(attributes.date, 8);
  const time = leadingZero(attributes.time, 5);

  const schedule = attributes.schedule.map((timeElement) => {
    const {
      booked,
      my,
      cancelled,
      lesson,
      student,
      status,
    } = timeElement;

    /**
     * Установка флага блокировки
     * если есть непосредственно флаг блокировки
     * или если есть запись на данное время и это время не текущего пользователя
     * и это не отменяемое событие
     * или если есть флаг отсутствия занятия у инструктора
     * или если активен фильтр и данное время не входит в его выборку
     */
    const isDisabled = (
      timeElement.disabled
      || (booked && !my && !cancelled)
      || (lesson === false)
    );

    /**
     * Установка флага активности выпадающего списка
     * если нет информация о студенте и нет блокировки
     * или есть флаг отменяемости события
     * или если есть информация о студенте и есть запись
     * или если есть информация о студенте и есть флаг занятия у инструктора
     */
    const isShowDropdown = (
      (!student && !isDisabled)
      || cancelled
      || (student && booked)
      || (student && [true, false].includes(lesson))
    );

    /**
     * Установка темы кнопки
     */
    const getButtonTheme = () => {
      if (student && !isShowDropdown) return 'silent';
      if (my || lesson) return 'hasLesson';
      return null;
    };
    const buttonTheme = getButtonTheme();

    /**
     * Установка ориентации текста кнопки
     */
    const buttonTextAlign = timeElement.qualityRating ? 'justify' : 'center';

    /**
     * Установка стиля кнопки для статусов инструктора
     */
    const buttonStyle = {};
    const buttonIconTheme = KEYS_MAP[status?.name] ?? null;

    /**
     * @TODO: фиксануть, когда с бэка будут приходить правильные комбинации данных
     * Установка внутреннего стиля кнопки
     */
    const buttonInnerStyle = {
      lineHeight: '20px',
      ...!buttonIconTheme && status?.color && {
        backgroundColor: status.color,
      },
    };

    const buttonProps = status?.name ? { title: status?.name } : null;

    /**
     * Установка outline стиля выпадающего списка
     */
    const dropdownIsOutline = (my || lesson);

    /**
     * Установка стилей выпадающего списка
     */
    const dropdownContentStyle = {};

    return {
      ...timeElement,
      ui: {
        ...timeElement.ui,
        isDisabled,
        isShowDropdown,
        buttonTheme,
        buttonTextAlign,
        buttonStyle,
        buttonInnerStyle,
        buttonProps,
        buttonIconTheme,
        dropdownIsOutline,
        dropdownContentStyle,
      },
    };
  });

  return {
    id,
    ...attributes,
    date,
    time,
    schedule,
  };
});

/**
 * Маппер календаря инструктора
 * Настройка специфичных флагов интерфейса и стилей
 * @param {Array} calendar
 * @returns {Array}
 */
export const calendarInstructorMapper = (calendar) => calendar.map((day) => {
  const schedule = day.schedule.map((timeElement) => {
    const { paid, qualityRating } = timeElement;

    /**
     * Стили для обналиченного занятия
     */
    const buttonInnerStyle = {
      ...timeElement.ui.buttonInnerStyle,
      ...paid && { color: '#28a745' },
    };
    const buttonProps = paid ? { title: 'Обналичено' } : null;
    const getIconTheme = () => {
      if (paid && !qualityRating) return ICON_THEMES.RECEIPT;
      return timeElement.ui.buttonIconTheme ?? null;
    };
    const buttonIconTheme = getIconTheme();

    return {
      ...timeElement,
      ui: {
        ...timeElement.ui,
        buttonInnerStyle,
        buttonProps,
        buttonIconTheme,
      },
    };
  });

  return {
    ...day,
    schedule,
  };
});

/**
 * Маппер календаря инструктора для админского интерфейса
 * Настройка специфичных флагов интерфейса и стилей
 * @param {Array} calendar
 * @returns {Array}
 */
export const calendarAdminInstructorMapper = (calendar) => calendar.map((day) => {
  const schedule = day.schedule.map((timeElement) => {
    const { lesson, paid, qualityRating } = timeElement;

    /**
     * Стили для обналиченного занятия
     */
    const buttonInnerStyle = {
      ...timeElement.ui.buttonInnerStyle,
      ...paid && { color: '#28a745' },
    };
    const getIconTheme = () => {
      if (lesson && !!qualityRating) return ICON_THEMES.RATING;
      if (lesson && !paid && !qualityRating) return ICON_THEMES.MONEY;
      if (lesson && paid && !qualityRating) return ICON_THEMES.RECEIPT;
      return timeElement.ui.buttonIconTheme ?? null;
    };
    const buttonIconTheme = getIconTheme();
    const buttonProps = paid ? { title: 'Обналичено' } : null;

    return {
      ...timeElement,
      ui: {
        ...timeElement.ui,
        isShowButtonBlock: lesson && !paid && !qualityRating,
        isShowLinkRating: lesson && qualityRating?.rating <= 3,
        buttonInnerStyle,
        buttonProps: {
          ...timeElement.ui.buttonProps,
          ...buttonProps,
        },
        buttonIconTheme,
      },
    };
  });

  return {
    ...day,
    schedule,
  };
});

/**
 * Маппер календаря студента для админского интерфейса
 * Отключение всего ненужного контента и интерактива
 * @param {Array} calendar
 * @returns {Array}
 */
export const calendarAdminStudentMapper = (calendar) => calendar.map((day) => {
  const schedule = day.schedule.map((timeElement) => ({
    ...timeElement,
    ui: {
      ...timeElement.ui,
      isShowDropdown: false,
      buttonProps: {
        ...timeElement.ui.buttonProps,
        tag: 'div',
        depressed: true,
      },
    },
  }));

  return {
    ...day,
    schedule,
  };
});

/**
 * @param {String} userType
 * @param {Array} calendar
 * @returns {Array}
 */
export const calendarMapper = (userType, calendar) => {
  const mapped = calendarDefaultMapper(calendar);

  const getPreparer = (type) => ({
    [USER_TYPES.STUDENT]: (payload) => payload,
    [USER_TYPES.INSTRUCTOR]: calendarInstructorMapper,
    [USER_TYPES.ADMIN_INFO_INSTRUCTOR]: (payload) => payload,
    [USER_TYPES.ADMIN_INSTRUCTOR]: calendarAdminInstructorMapper,
    [USER_TYPES.ADMIN_STUDENT]: calendarAdminStudentMapper,
    [USER_TYPES.REEGISTRATION_STUDENT]: (payload) => payload,
  }[type]);

  return getPreparer(userType)(mapped);
};

/**
 * @param {Array} history
 * @returns {Array}
 */
export const historyMapper = (history) => history.map(({ id, attributes }) => {
  const date = leadingZero(attributes.date, 8);
  const time = leadingZero(attributes.time, 5);
  return {
    id,
    ...attributes,
    date,
    time,
  };
});

/**
 * @param {Array} rateHistory
 * @returns {Array}
 */
export const rateHistoryMapper = (rateHistory) => rateHistory.map(({ id, attributes }) => {
  const date = leadingZero(attributes.date, 8);
  return {
    id,
    ...attributes,
    date,
  };
});

/**
 * @param {Object} qualityRating
 * @returns {Object}
 */
export const qualityRatingMapper = (qualityRating) => ({
  heading: qualityRating.Heading,
  description: qualityRating.Description,
  complaints: qualityRating.preAnswer,
  date: qualityRating.Date,
  timeFrom: qualityRating.TimeFrom,
  timeTo: qualityRating.TimeTo,
  lessonId: qualityRating.LessonID,
  instructorId: qualityRating.InstructorID,
  instructorName: qualityRating.InstructorName,
});
